import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, type ActionButton } from 'components/modals';
import { SyutSelect, type SelectOption } from 'components/ui';
import { EnterpriseScreenShell, EnterpriseFormModal, actionButtonFactory, useCan } from 'components/enterprise-shell';
import type { FormModalMode } from 'components/enterprise-shell';
import TaxMasterPicker from 'components/shared/tax-master-module/TaxMasterPicker';
import taxMasterService from 'api/services/taxMasterService';
import notificationService from 'services/notificationService';
import { useScopeContext } from 'hooks/usePermissions';
import {
  entityAccountingConfigService, taxMetaService,
  entityTaxRegistrationService, entityTaxAccountService, entityTaxConfigService, coaLedgerService,
  taxProfileService, taxRegistrationStatusService,
} from 'api/services';
import type {
  EntityAccountingConfigDto, TaxJurisdictionDto, TaxComponentDto, TaxRateLookupDto, TaxFieldContextDto,
  EntityTaxRegistrationDto, EntityTaxAccountDto, EntityTaxConfigDto, TaxRegistrationStatusDto, CreateEntityTaxRegistrationRequest,
} from 'api/types';
import TaxFieldsPanel, { validateTaxFields, type TaxFieldValues } from './TaxFieldsPanel';
import RegistrationStatusCard from './RegistrationStatusCard';

// ============================================================================
// Tax Settings — config-style screen (left-nav sections + right content), matching
// Entity Accounting Config. ADR-0047 Phase 2 §6 generalised it across jurisdictions:
//
//   Jurisdiction   the entity's regime (drives everything below)
//   Registrations  the entity's registration LIST — one per state under GST, one VAT number
//                  under Botswana — with the business units that issue under each and the
//                  entity default. The fields come from the jurisdiction's REGISTRATION field
//                  schema (GSTIN + state + type vs a VAT number), not from code.
//   Defaults       the default tax master that pre-fills a new item's mapping, the bulk
//                  "apply it to every unmapped item" action, and whether an item's
//                  classification must exist in the classification master (ADR-0047 §7)
//   Tax Ledgers    one OUTPUT / INPUT pair PER COMPONENT (VAT, or CGST/SGST/IGST/CESS)
//   Rates          read-only
//
// Registrations / Defaults / Ledgers / Rates are disabled until a jurisdiction is set.
// ============================================================================

type SectionId = 'jurisdiction' | 'registrations' | 'defaults' | 'ledgers' | 'rates';
const SECTIONS: { id: SectionId; label: string; icon: keyof typeof IconSystem; desc: string; needsJurisdiction?: boolean }[] = [
  { id: 'jurisdiction', label: 'Jurisdiction', icon: 'tag', desc: 'The tax regime for this entity' },
  { id: 'registrations', label: 'Registrations', icon: 'file', desc: 'The registrations this entity holds, and which business units issue under each', needsJurisdiction: true },
  { id: 'defaults', label: 'Defaults', icon: 'check', desc: 'What a new item is mapped to unless you choose otherwise', needsJurisdiction: true },
  { id: 'ledgers', label: 'Tax Ledgers', icon: 'list', desc: 'GL ledgers the engine posts each tax component to', needsJurisdiction: true },
  { id: 'rates', label: 'Rates', icon: 'percent', desc: 'Tax masters in force today (read-only; maintained under Tax Masters)', needsJurisdiction: true },
];

const REGISTRATION_TARGET = 'REGISTRATION';
const NUMBER_BINDING = 'REGISTRATION.NUMBER';
const STATE_BINDING = 'REGISTRATION.STATE';
const TYPE_BINDING = 'REGISTRATION.TYPE';

// A label + description on the left, control on the right — one settings row.
const SettingRow: React.FC<{ label: React.ReactNode; description?: string; children: React.ReactNode }> = ({ label, description, children }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1.5rem', padding: '0.9rem 0', borderBottom: '1px solid var(--border-color)', flexWrap: 'wrap' }}>
    <div style={{ minWidth: 220, flex: 1 }}>
      <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)' }}>{label}</div>
      {description && <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.15rem' }}>{description}</div>}
    </div>
    <div style={{ minWidth: 300, maxWidth: 380, width: 340 }}>{children}</div>
  </div>
);

const fmtDate = (d?: string | null) => (d ? d.slice(0, 10) : '');

/** The registration form: the schema-driven values plus the fields every jurisdiction shares. */
interface RegistrationForm {
  label: string;
  /** Used only when the jurisdiction declares no REGISTRATION.NUMBER field. */
  registrationNumber: string;
  isDefault: boolean;
  effectiveFrom: string;
  effectiveTo: string;
  businessUnitIds: string[];
  tax: TaxFieldValues;
}

const EMPTY_REG_FORM: RegistrationForm = {
  label: '', registrationNumber: '', isDefault: false, effectiveFrom: '', effectiveTo: '', businessUnitIds: [], tax: {},
};

interface TaxSettingsScreenProps {
  moduleIcon?: string;
  moduleName?: string;
  tabId?: string;
}

const TaxSettingsScreen: React.FC<TaxSettingsScreenProps> = ({ moduleIcon, moduleName = 'Tax Settings', tabId }) => {
  const can = useCan('tax-settings');
  const canEdit = can('UPDATE');
  const canCreate = can('CREATE') || canEdit;
  const canDelete = can('DELETE');
  const { selectedEntity, selectedBusinessUnit, accessibleBusinessUnits } = useScopeContext();

  const [activeSection, setActiveSection] = useState<SectionId>('jurisdiction');
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState<EntityAccountingConfigDto | null>(null);
  const [jurisdictions, setJurisdictions] = useState<TaxJurisdictionDto[]>([]);
  const [jurisdictionCode, setJurisdictionCode] = useState('');
  const [jurisdiction, setJurisdiction] = useState<TaxJurisdictionDto | null>(null);
  const [savingJur, setSavingJur] = useState(false);

  const [components, setComponents] = useState<TaxComponentDto[]>([]);
  const [rates, setRates] = useState<TaxRateLookupDto[]>([]);

  // Registrations
  const [registrations, setRegistrations] = useState<EntityTaxRegistrationDto[]>([]);
  const [regContext, setRegContext] = useState<TaxFieldContextDto | null>(null);
  const [regModalOpen, setRegModalOpen] = useState(false);
  const [regModalMode, setRegModalMode] = useState<FormModalMode>('create');
  const [regRecord, setRegRecord] = useState<EntityTaxRegistrationDto | null>(null);
  const [regToDelete, setRegToDelete] = useState<EntityTaxRegistrationDto | null>(null);
  const [deletingReg, setDeletingReg] = useState(false);
  const regCreateCounter = useRef(0);

  // Defaults
  const [taxConfig, setTaxConfig] = useState<EntityTaxConfigDto | null>(null);
  const [defaultRateCode, setDefaultRateCode] = useState('');
  const [defaultRateName, setDefaultRateName] = useState('');
  const [classificationEnforced, setClassificationEnforced] = useState(false);
  // The registration status timeline (ADR-0048 §1 amended) — shown at the top of Registrations.
  const [statusInfo, setStatusInfo] = useState<TaxRegistrationStatusDto | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [savingDefaults, setSavingDefaults] = useState(false);
  const [defaultsConfirmOpen, setDefaultsConfirmOpen] = useState(false);
  const [applyConfirmOpen, setApplyConfirmOpen] = useState(false);
  const [applying, setApplying] = useState(false);

  // Ledgers — keyed `${componentId}|${direction}`
  const [accounts, setAccounts] = useState<EntityTaxAccountDto[]>([]);
  const [ledgerChoice, setLedgerChoice] = useState<Record<string, string>>({});
  const [savingLedgers, setSavingLedgers] = useState(false);
  const [ledgersConfirmOpen, setLedgersConfirmOpen] = useState(false);
  const ledgerCache = useRef<Map<string, string>>(new Map());

  const [jurConfirmOpen, setJurConfirmOpen] = useState(false);

  const ledgerKey = (componentId: string | null | undefined, direction: string) => `${componentId || ''}|${direction}`;

  const loadTaxData = useCallback(async () => {
    const jd = await taxMetaService.getEntityJurisdiction().catch(() => null);
    setJurisdiction(jd);
    if (!jd) {
      setRegistrations([]); setRegContext(null); setAccounts([]); setLedgerChoice({}); setRates([]); setComponents([]);
      setTaxConfig(null); setDefaultRateCode(''); setDefaultRateName(''); setClassificationEnforced(false);
      return;
    }
    const [regs, accs, comps, rt, ctx, cfg, st] = await Promise.all([
      entityTaxRegistrationService.getAll({ pageSize: 100 }),
      entityTaxAccountService.getAll({ pageSize: 100 }),
      taxMetaService.getComponents(),
      taxMetaService.getRates(),
      taxMetaService.getFieldContext(REGISTRATION_TARGET).catch(() => null),
      entityTaxConfigService.get().catch(() => null),
      taxRegistrationStatusService.get().catch(() => null),
    ]);
    setRegistrations(regs.data);
    setStatusInfo(st);
    setRegContext(ctx);
    setComponents(comps);
    setRates(rt);
    setAccounts(accs.data);
    const choice: Record<string, string> = {};
    accs.data.forEach(a => {
      if (a.ledgerId && a.ledgerName) ledgerCache.current.set(a.ledgerId, `${a.ledgerCode} - ${a.ledgerName}`);
      choice[ledgerKey(a.componentId, a.transactionType)] = a.ledgerId;
    });
    setLedgerChoice(choice);
    setTaxConfig(cfg);
    setDefaultRateCode(cfg?.defaultRateCode || '');
    setDefaultRateName(cfg?.defaultRateName || '');
    setClassificationEnforced(!!cfg?.classificationEnforced);
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, juris] = await Promise.all([entityAccountingConfigService.getConfig(), taxMetaService.getJurisdictions()]);
      setConfig(cfg); setJurisdictionCode(cfg.jurisdictionCode || ''); setJurisdictions(juris);
      await loadTaxData();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not load tax settings.');
    } finally { setLoading(false); }
  }, [loadTaxData]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // --- Jurisdiction --- (preserve currency/receivables on save)
  const jurDirty = !!config && (jurisdictionCode || '') !== (config.jurisdictionCode || '');
  const handleConfirmSaveJurisdiction = useCallback(async () => {
    setSavingJur(true);
    try {
      const saved = await entityAccountingConfigService.saveConfig({
        // Currency is the accounting-engine invariant; user must set it in
        // Entity Accounting Config → Functional Currency before jurisdiction
        // can be saved. Sending empty here surfaces a 400 from the backend
        // with the correct "Functional currency is required" message.
        functionalCurrencyCode: config?.functionalCurrencyCode || '',
        jurisdictionCode: jurisdictionCode || null,
        receivablesSubtypeId: config?.receivablesSubtypeId ?? null,
        receivablesGroupId: config?.receivablesGroupId ?? null,
        // Carried through — a jurisdiction save must not reset the ADR-0026 toggle.
        balancedBuPosting: config?.balancedBuPosting ?? false,
        rowVersion: config?.rowVersion ?? 0,
      });
      setConfig(saved); setJurisdictionCode(saved.jurisdictionCode || '');
      setSavingJur(false);
      setJurConfirmOpen(false);
      window.dispatchEvent(new Event('fbp:setup-changed'));
      notificationService.toast.success('Tax jurisdiction saved.');
      await loadTaxData();
    } catch (err: any) {
      setSavingJur(false);
      notificationService.toast.error(err?.response?.data?.message || 'Could not save the tax jurisdiction.');
    }
  }, [jurisdictionCode, config, loadTaxData]);

  // --- Registrations ---
  const regFields = regContext?.fields || [];
  const numberField = regFields.find(f => f.binding === NUMBER_BINDING);
  const schemaHasNumber = !!numberField;
  const entityBusinessUnits = useMemo<SelectOption[]>(() => (accessibleBusinessUnits || [])
    .filter(bu => !selectedEntity || bu.entityId === selectedEntity.id)
    .map(bu => ({ value: bu.id, label: `${bu.buCode} - ${bu.buName}` })), [accessibleBusinessUnits, selectedEntity]);

  // The unit the user is working in, and the registration it issues under (own link, else the
  // entity default, else the only one) — the same rule the calculation applies. Registration is
  // entity-level; this line is what the unit-level login sees of it.
  const unitCue = useMemo<string | null>(() => {
    if (!selectedBusinessUnit || registrations.length === 0) return null;
    const linked = registrations.find(r => (r.businessUnits || []).some(b => b.id === selectedBusinessUnit.id));
    const issuing = linked ?? registrations.find(r => r.isDefault) ?? (registrations.length === 1 ? registrations[0] : undefined);
    if (!issuing) return `You are working in ${selectedBusinessUnit.name}, which is linked to no registration and the entity has no default — link it below.`;
    const how = linked ? 'its own link' : registrations.length === 1 ? 'the only registration' : 'the entity default';
    return `You are working in ${selectedBusinessUnit.name}, which issues under ${issuing.label || issuing.registrationNumber} (${how}).`;
  }, [selectedBusinessUnit, registrations]);

  const openAddRegistration = () => {
    regCreateCounter.current += 1;
    setRegRecord(null); setRegModalMode('create'); setRegModalOpen(true);
  };
  const openEditRegistration = (r: EntityTaxRegistrationDto) => {
    setRegRecord(r); setRegModalMode('edit'); setRegModalOpen(true);
  };

  const populateRegistration = useCallback((mode: FormModalMode, record: EntityTaxRegistrationDto | null): RegistrationForm => {
    if (!record) {
      // First registration of an entity is naturally its default.
      return { ...EMPTY_REG_FORM, isDefault: registrations.length === 0, tax: {} };
    }
    return {
      label: record.label || '',
      registrationNumber: record.registrationNumber || '',
      isDefault: record.isDefault,
      effectiveFrom: fmtDate(record.effectiveFrom),
      effectiveTo: fmtDate(record.effectiveTo),
      businessUnitIds: (record.businessUnits || []).map(b => b.id),
      tax: { ...(record.attributes || {}) },
    };
  }, [registrations.length]);

  const validateRegistration = useCallback((form: RegistrationForm): Record<string, string> => {
    const errors: Record<string, string> = {};
    // Numbers and types are uppercase identities (the server uppercases them before its own
    // pattern check); mirror that so a GSTIN typed in lowercase is not refused for its case.
    const upper = { ...form.tax };
    regFields.filter(f => f.binding === NUMBER_BINDING || f.binding === TYPE_BINDING)
      .forEach(f => { const v = upper[f.fieldKey]; if (typeof v === 'string') upper[f.fieldKey] = v.toUpperCase(); });
    const taxErrors = validateTaxFields(regContext, upper);
    Object.entries(taxErrors).forEach(([k, v]) => { errors[`tax.${k}`] = v; });
    if (!schemaHasNumber && !form.registrationNumber.trim()) errors.registrationNumber = 'Registration number is required.';
    if (form.effectiveFrom && form.effectiveTo && form.effectiveTo < form.effectiveFrom) errors.effectiveTo = 'Effective-to cannot precede effective-from.';
    return errors;
  }, [regContext, regFields, schemaHasNumber]);

  const buildRegistrationRequest = useCallback((form: RegistrationForm): CreateEntityTaxRegistrationRequest => {
    // Column-bound fields are derived from the schema values; the server does the same routing,
    // so the explicit properties only matter for a jurisdiction that declares no such field.
    const bound = (binding: string) => {
      const f = regFields.find(x => x.binding === binding);
      const v = f ? form.tax[f.fieldKey] : undefined;
      return v === undefined || v === null || v === '' ? null : String(v);
    };
    return {
      jurisdictionId: jurisdiction!.id,
      label: form.label.trim() || null,
      registrationNumber: (bound(NUMBER_BINDING) ?? form.registrationNumber).trim(),
      registrationType: bound(TYPE_BINDING),
      stateProvinceId: bound(STATE_BINDING),
      isDefault: form.isDefault,
      effectiveFrom: form.effectiveFrom || null,
      effectiveTo: form.effectiveTo || null,
      attributes: form.tax,
      businessUnitIds: form.businessUnitIds,
      statusCode: 'ACTIVE',
    };
  }, [regFields, jurisdiction]);

  const saveRegistration = useCallback(async (form: RegistrationForm, mode: FormModalMode, record: EntityTaxRegistrationDto | null) => {
    const req = buildRegistrationRequest(form);
    if (mode === 'edit' && record) {
      await entityTaxRegistrationService.update(record.id, { ...req, rowVersion: record.rowVersion });
    } else {
      await entityTaxRegistrationService.create(req);
    }
  }, [buildRegistrationRequest]);

  const makeDefault = useCallback(async (r: EntityTaxRegistrationDto) => {
    try {
      await entityTaxRegistrationService.update(r.id, {
        jurisdictionId: r.jurisdictionId, label: r.label ?? null, registrationNumber: r.registrationNumber,
        registrationType: r.registrationType ?? null, stateProvinceId: r.stateProvinceId ?? null, isDefault: true,
        effectiveFrom: r.effectiveFrom ?? null, effectiveTo: r.effectiveTo ?? null,
        attributes: r.attributes || {}, businessUnitIds: (r.businessUnits || []).map(b => b.id),
        statusCode: 'ACTIVE', rowVersion: r.rowVersion,
      });
      notificationService.toast.success(`${r.label || r.registrationNumber} is now the default registration.`);
      await loadTaxData();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not change the default registration.');
    }
  }, [loadTaxData]);

  const handleConfirmDeleteRegistration = useCallback(async () => {
    if (!regToDelete) return;
    setDeletingReg(true);
    try {
      await entityTaxRegistrationService.delete(regToDelete.id, regToDelete.rowVersion);
      notificationService.toast.success('Registration deleted.');
      setRegToDelete(null);
      await loadTaxData();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not delete the registration.');
    } finally { setDeletingReg(false); }
  }, [regToDelete, loadTaxData]);

  // --- Defaults ---
  const defaultsDirty = (defaultRateCode || '') !== (taxConfig?.defaultRateCode || '')
    || classificationEnforced !== !!taxConfig?.classificationEnforced;
  const handleConfirmSaveDefaults = useCallback(async () => {
    setSavingDefaults(true);
    try {
      const saved = await entityTaxConfigService.upsert({
        defaultRateCode: defaultRateCode || null, classificationEnforced, rowVersion: taxConfig?.rowVersion ?? 0,
      });
      setTaxConfig(saved);
      setDefaultRateCode(saved.defaultRateCode || ''); setDefaultRateName(saved.defaultRateName || '');
      setClassificationEnforced(!!saved.classificationEnforced);
      setDefaultsConfirmOpen(false);
      window.dispatchEvent(new Event('fbp:setup-changed'));
      notificationService.toast.success('Tax defaults saved.');
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not save the tax defaults.');
    } finally { setSavingDefaults(false); }
  }, [defaultRateCode, classificationEnforced, taxConfig]);

  // Pin every unmapped item to the SAVED default — a one-time close-out for items created
  // before the default existed (fix 021 migrated the rest). New items pre-fill on creation.
  const handleConfirmApplyDefault = useCallback(async () => {
    setApplying(true);
    try {
      const result = await taxProfileService.applyDefaultToUnmapped();
      setApplyConfirmOpen(false);
      notificationService.toast.success(result.mappedCount === 0
        ? 'Every item is already mapped.'
        : `${result.mappedCount} item(s) mapped to ${result.rateCode}.${result.remainingUnmapped ? ` ${result.remainingUnmapped} still unmapped.` : ''}`);
      const cfg = await entityTaxConfigService.get().catch(() => null);
      if (cfg) setTaxConfig(cfg);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not apply the default tax master.');
    } finally { setApplying(false); }
  }, []);

  // --- Tax ledgers (GL mapping) — one OUTPUT / INPUT pair per component ---
  const ledgersDirty = useMemo(() => components.some(c => ['OUTPUT', 'INPUT'].some(d => {
    const key = ledgerKey(c.id, d);
    const saved = accounts.find(a => a.componentId === c.id && a.transactionType === d)?.ledgerId || '';
    return (ledgerChoice[key] || '') !== saved;
  })), [components, accounts, ledgerChoice]);

  const loadLedgers = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      // Only ledgers of nature Tax Account (ACC_LEDGER_NATURE = TAX): a component posts to
      // nothing else, and the server refuses any other nature.
      const res = await coaLedgerService.getAll({ pageSize: 50, searchTerm: input || undefined, ledgerNature: 'TAX' });
      res.data.forEach(l => ledgerCache.current.set(l.id, `${l.ledgerCode} - ${l.ledgerName}`));
      return res.data.map(l => ({ value: l.id, label: `${l.ledgerCode} - ${l.ledgerName}` }));
    } catch {
      return [];
    }
  }, []);

  const handleConfirmSaveLedgers = useCallback(async () => {
    if (!jurisdiction) return;
    setSavingLedgers(true);
    try {
      for (const c of components) {
        for (const direction of ['OUTPUT', 'INPUT']) {
          const ledgerId = ledgerChoice[ledgerKey(c.id, direction)] || '';
          const acc = accounts.find(a => a.componentId === c.id && a.transactionType === direction) || null;
          if (acc) {
            if (!ledgerId || ledgerId === acc.ledgerId) continue; // leave as-is if cleared (no delete from settings)
            await entityTaxAccountService.update(acc.id, {
              jurisdictionId: jurisdiction.id, componentId: c.id, transactionType: direction, ledgerId,
              effectiveFrom: acc.effectiveFrom || undefined, effectiveTo: acc.effectiveTo || undefined, statusCode: 'ACTIVE', rowVersion: acc.rowVersion,
            });
          } else if (ledgerId) {
            await entityTaxAccountService.create({ jurisdictionId: jurisdiction.id, componentId: c.id, transactionType: direction, ledgerId, statusCode: 'ACTIVE' });
          }
        }
      }
      setLedgersConfirmOpen(false);
      notificationService.toast.success('Tax ledgers saved.');
      await loadTaxData();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not save the tax ledgers.');
    } finally { setSavingLedgers(false); }
  }, [jurisdiction, components, accounts, ledgerChoice, loadTaxData]);

  const actionButtons: ActionButton[] = [
    actionButtonFactory.refresh(loadAll),
  ];
  const fmtPct = (n: number) => `${n.toFixed(2)}%`;

  // Dependent sections collapse back to Jurisdiction until one is set.
  const effectiveSection: SectionId = (!jurisdiction && activeSection !== 'jurisdiction') ? 'jurisdiction' : activeSection;
  const sectionMeta = SECTIONS.find(s => s.id === effectiveSection)!;

  return (
    <EnterpriseScreenShell
      title="Tax Settings"
      description="Jurisdiction, registrations, defaults and the tax ledgers for this entity"
      icon={moduleIcon && (IconSystem as any)[moduleIcon]
        ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
        : <IconSystem.settings size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
    >
      <div style={{
        flex: 1, display: 'flex', overflow: 'hidden',
        border: '1px solid var(--border-color)', borderRadius: '0.75rem',
        background: 'var(--bg-primary)', minHeight: 0,
      }}>
          {/* Left nav */}
          <div style={{ width: 240, flexShrink: 0, borderRight: '1px solid var(--border-color)', padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.25rem', overflow: 'auto' }}>
            {SECTIONS.map(s => {
              const Icon = IconSystem[s.icon] as React.FC<{ size?: number }>;
              const disabled = !!s.needsJurisdiction && !jurisdiction;
              const active = effectiveSection === s.id;
              return (
                <div key={s.id} role="button" tabIndex={disabled ? -1 : 0}
                  onClick={() => { if (!disabled) setActiveSection(s.id); }}
                  onKeyDown={e => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setActiveSection(s.id); } }}
                  title={disabled ? 'Set the tax jurisdiction first' : undefined}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.6rem 0.75rem', borderRadius: '0.5rem',
                    cursor: disabled ? 'not-allowed' : 'pointer', userSelect: 'none', opacity: disabled ? 0.45 : 1,
                    borderLeft: active ? '3px solid var(--primary)' : '3px solid transparent',
                    background: active ? 'var(--bg-secondary)' : 'transparent',
                    color: active ? 'var(--primary)' : 'var(--text-primary)', fontWeight: active ? 600 : 500 }}>
                  {Icon && <Icon size={18} />}
                  <span style={{ fontSize: '0.875rem' }}>{s.label}</span>
                </div>
              );
            })}
          </div>

          {/* Right content */}
          <div style={{ flex: 1, overflow: 'auto', padding: '1.5rem 1.75rem', minWidth: 0 }}>
            <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.15rem', fontWeight: 700, color: 'var(--text-primary)' }}>{sectionMeta.label}</h2>
            <p style={{ margin: '0 0 1.25rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{sectionMeta.desc}</p>

            {/* Jurisdiction */}
            {effectiveSection === 'jurisdiction' && (
              <>
                <SettingRow label="Tax Jurisdiction" description="The tax regime for this entity (e.g. Botswana VAT, India GST). Drives the jurisdiction-aware tax fields on the masters and the tax calculation.">
                  <SyutSelect menuPortalTarget={document.body}
                    options={jurisdictions.map(j => ({ value: j.jurisdictionCode, label: `${j.jurisdictionName} (${j.jurisdictionCode})` }))}
                    value={jurisdictionCode} onChange={(v) => setJurisdictionCode((v as string) || '')} placeholder="Not set" isClearable />
                </SettingRow>
                {!loading && !config?.functionalCurrencyCode && (
                  <div style={{
                    marginTop: '1rem', padding: '0.75rem 1rem', borderRadius: '0.5rem',
                    background: 'var(--bg-warning, #fff4e5)', border: '1px solid var(--border-warning, #f0b878)',
                    color: 'var(--text-warning, #7a4b00)', fontSize: '0.85rem', lineHeight: 1.5,
                  }}>
                    <strong>Functional currency is not set for this entity.</strong> Configure it under
                    {' '}<em>Accounts &rarr; Entity Accounting Config &rarr; Functional Currency</em> before saving a tax jurisdiction &mdash;
                    the accounting engine treats currency as an invariant and locks the field once posted transactions exist.
                  </div>
                )}
                <div style={{ marginTop: '1rem' }}>
                  <button className="btn btn-primary" onClick={() => setJurConfirmOpen(true)}
                    disabled={!jurDirty || savingJur || !canEdit || !config?.functionalCurrencyCode}
                    title={!config?.functionalCurrencyCode ? 'Set the functional currency in Entity Accounting Config first' : undefined}>
                    {savingJur ? 'Saving…' : 'Save'}
                  </button>
                </div>
                {!loading && !jurisdiction && (
                  <div style={{ marginTop: '1rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>Set the tax jurisdiction to unlock registrations, defaults, tax ledgers and rates.</div>
                )}
              </>
            )}

            {/* Registrations */}
            {effectiveSection === 'registrations' && jurisdiction && (
              <>
                <RegistrationStatusCard
                  jurisdictionName={jurisdiction.jurisdictionName}
                  info={statusInfo}
                  canEdit={canEdit}
                  unitCue={unitCue}
                  onChanged={(next) => { setStatusInfo(next); void loadTaxData(); }}
                />
                {statusInfo?.latest?.status === 'REGISTERED' && (
                <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', maxWidth: 640 }}>
                    {jurisdiction.isStateBased
                      ? 'A registration is held per state. Each business unit issues documents under the registration it is linked to; a business unit without a link uses the default.'
                      : 'Documents issue under the default registration unless a business unit is linked to another one.'}
                  </div>
                  <button className="btn btn-primary" onClick={openAddRegistration} disabled={!canCreate}>
                    + Add Registration
                  </button>
                </div>
                {registrations.length === 0 && (
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', padding: '1rem 0' }}>
                    No registration yet. Add one — tax cannot be computed for a state-based jurisdiction until the entity has a registration with a state.
                  </div>
                )}
                {registrations.length > 0 && (
                  <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ textAlign: 'left', color: 'var(--text-secondary)' }}>
                        <th style={{ padding: '0.5rem' }}>Registration</th>
                        {jurisdiction.isStateBased && <th style={{ padding: '0.5rem' }}>State</th>}
                        <th style={{ padding: '0.5rem' }}>Type</th>
                        <th style={{ padding: '0.5rem' }}>Business units</th>
                        <th style={{ padding: '0.5rem' }}>Effective</th>
                        <th style={{ padding: '0.5rem', width: 190 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {registrations.map(r => (
                        <tr key={r.id} style={{ borderTop: '1px solid var(--border-color)' }}>
                          <td style={{ padding: '0.5rem' }}>
                            <div style={{ fontWeight: 600 }}>
                              {r.label || r.registrationNumber}
                              {r.isDefault && <span className="badge badge-primary" style={{ marginLeft: '0.5rem', fontSize: '0.7rem' }}>Default</span>}
                            </div>
                            {r.label && <div style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-mono, monospace)' }}>{r.registrationNumber}</div>}
                          </td>
                          {jurisdiction.isStateBased && (
                            <td style={{ padding: '0.5rem' }}>
                              {r.stateProvinceName || <span className="text-danger">not set</span>}
                              {r.taxStateCode && <span style={{ color: 'var(--text-muted)' }}> ({r.taxStateCode})</span>}
                            </td>
                          )}
                          <td style={{ padding: '0.5rem' }}>{r.registrationType || ''}</td>
                          <td style={{ padding: '0.5rem' }}>
                            {(r.businessUnits || []).length === 0
                              ? <span style={{ color: 'var(--text-muted)' }}>{r.isDefault ? 'all unlinked units' : '—'}</span>
                              : r.businessUnits.map(b => b.unitCode || b.unitName).join(', ')}
                          </td>
                          <td style={{ padding: '0.5rem', whiteSpace: 'nowrap' }}>
                            {fmtDate(r.effectiveFrom) || 'open'}{' → '}{fmtDate(r.effectiveTo) || 'open'}
                          </td>
                          <td style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                            {!r.isDefault && canEdit && (
                              <button className="btn btn-link btn-sm" onClick={() => makeDefault(r)} title="Make this the default registration">Set default</button>
                            )}
                            <button className="btn btn-link btn-sm" onClick={() => openEditRegistration(r)} disabled={!canEdit}>Edit</button>
                            <button className="btn btn-link btn-sm text-danger" onClick={() => setRegToDelete(r)} disabled={!canDelete}>Delete</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                </>
                )}
              </>
            )}

            {/* Defaults */}
            {effectiveSection === 'defaults' && jurisdiction && (
              <>
                {statusInfo?.currentStatus === 'NOT_REGISTERED' && (
                  <div style={{ marginBottom: '0.75rem', padding: '0.6rem 0.75rem', borderRadius: '0.5rem', background: 'var(--bg-secondary, #f8fafc)', border: '1px solid var(--border-color)', fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                    The entity is <strong>not registered</strong> for {jurisdiction.jurisdictionName} (see <em>Registrations</em>): documents charge no tax, so these defaults are not applied until it registers.
                  </div>
                )}
                <SettingRow label="Default tax master"
                  description="Pre-fills the tax master on a new item under this jurisdiction. It is a starting point, not a fallback: an item that is still unmapped when a document is posted is an error, never silently taxed at this rate.">
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {defaultRateCode
                        ? <div><div style={{ fontWeight: 600 }}>{defaultRateName || defaultRateCode}</div><div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono, monospace)' }}>{defaultRateCode}</div></div>
                        : <span style={{ color: 'var(--text-muted)' }}>Not set</span>}
                    </div>
                    <button className="btn btn-secondary btn-sm" onClick={() => setPickerOpen(true)} disabled={!canEdit}>Choose…</button>
                    {defaultRateCode && <button className="btn btn-link btn-sm" onClick={() => { setDefaultRateCode(''); setDefaultRateName(''); }} disabled={!canEdit}>Clear</button>}
                  </div>
                </SettingRow>
                <SettingRow label="Unmapped items"
                  description="Items with no tax master under this jurisdiction. They cannot be put on a document until mapped — here in one go with the saved default, or one by one under Service Master.">
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                      {(taxConfig?.unmappedItemCount ?? 0) > 0
                        ? <span className="badge" style={{ background: 'var(--danger-bg, #fee2e2)', color: 'var(--danger, #b91c1c)' }}>{taxConfig!.unmappedItemCount} unmapped</span>
                        : <span style={{ color: 'var(--text-muted)' }}>Every item is mapped</span>}
                    </div>
                    <button className="btn btn-secondary btn-sm" onClick={() => setApplyConfirmOpen(true)}
                      disabled={!canEdit || applying || !(taxConfig?.defaultRateCode) || defaultsDirty || (taxConfig?.unmappedItemCount ?? 0) === 0}
                      title={!taxConfig?.defaultRateCode ? 'Save a default tax master first' : defaultsDirty ? 'Save the defaults first' : undefined}>
                      Apply default to unmapped
                    </button>
                  </div>
                </SettingRow>
                <SettingRow label="Enforce the classification master"
                  description="When on, an item may only carry a classification code (HSN / SAC) that exists under Tax → Classifications. When off, any code is accepted; the master still supplies descriptions and default units where it knows the code. Only jurisdictions that declare a classification field are affected.">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: canEdit ? 'pointer' : 'default', fontSize: '0.85rem' }}>
                    <input type="checkbox" checked={classificationEnforced} disabled={!canEdit} onChange={e => setClassificationEnforced(e.target.checked)} />
                    Refuse codes not in the master
                  </label>
                </SettingRow>
                <div style={{ marginTop: '1rem' }}>
                  <button className="btn btn-primary" onClick={() => setDefaultsConfirmOpen(true)} disabled={!defaultsDirty || savingDefaults || !canEdit}>{savingDefaults ? 'Saving…' : 'Save'}</button>
                </div>
              </>
            )}

            {/* Tax ledgers — per component */}
            {effectiveSection === 'ledgers' && jurisdiction && (
              <>
                {components.length === 0 && <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>This jurisdiction publishes no tax components.</div>}
                {components.map(c => (
                  <div key={c.id} style={{ marginBottom: '0.5rem' }}>
                    {components.length > 1 && (
                      <div style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', margin: '1rem 0 0' }}>
                        {c.componentCode} · {c.componentName}
                      </div>
                    )}
                    {(['OUTPUT', 'INPUT'] as const).map(direction => {
                      const key = ledgerKey(c.id, direction);
                      const value = ledgerChoice[key] || '';
                      return (
                        <SettingRow key={key}
                          label={`${direction === 'OUTPUT' ? 'Output' : 'Input'} ${c.componentCode} ledger`}
                          description={direction === 'OUTPUT'
                            ? `${c.componentCode} on sales (payable) — where the engine credits output tax.`
                            : `${c.componentCode} on purchases (receivable) — where the engine debits input tax.`}>
                          <SyutSelect isAsync loadOptions={loadLedgers} defaultOptions cacheOptions={false}
                            seedOptions={value ? [{ value, label: ledgerCache.current.get(value) || '' }] : undefined}
                            menuPortalTarget={document.body} value={value}
                            onChange={v => setLedgerChoice(prev => ({ ...prev, [key]: (v as string) || '' }))}
                            placeholder={direction === 'OUTPUT' ? 'Tax Account ledgers only, e.g. Tax Payable' : 'Tax Account ledgers only, e.g. Tax Receivable'}
                            noOptionsMessage="No ledger of nature Tax Account matches. Set the nature under Ledger Accounts first."
                            isClearable isDisabled={!canEdit} />
                        </SettingRow>
                      );
                    })}
                  </div>
                ))}
                <div style={{ marginTop: '1rem' }}>
                  <button className="btn btn-primary" onClick={() => setLedgersConfirmOpen(true)} disabled={!ledgersDirty || savingLedgers || !canEdit}>{savingLedgers ? 'Saving…' : 'Save'}</button>
                </div>
              </>
            )}

            {/* Rates (read-only) */}
            {effectiveSection === 'rates' && jurisdiction && (
              <>
                {rates.length === 0 && <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>No rates in force. Author tax masters under Tax › Tax Masters.</div>}
                {rates.length > 0 && (
                  <table style={{ width: '100%', maxWidth: 560, fontSize: '0.85rem', borderCollapse: 'collapse' }}>
                    <thead><tr style={{ textAlign: 'left', color: 'var(--text-secondary)' }}>
                      <th style={{ padding: '0.5rem' }}>Master</th>
                      <th style={{ padding: '0.5rem' }}>Component</th>
                      <th style={{ padding: '0.5rem' }}>Category</th>
                      <th style={{ padding: '0.5rem', textAlign: 'right' }}>Rate</th>
                    </tr></thead>
                    <tbody>
                      {rates.map(r => (
                        <tr key={r.id} style={{ borderTop: '1px solid var(--border-color)' }}>
                          <td style={{ padding: '0.5rem' }}>{r.description || ''}</td>
                          <td style={{ padding: '0.5rem' }}>{r.componentCode || ''}</td>
                          <td style={{ padding: '0.5rem' }}>{r.supplyCategory}</td>
                          <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmtPct(r.ratePercentage)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )}
          </div>
        </div>

      {/* Registration add / edit */}
      {jurisdiction && (
        <EnterpriseFormModal<RegistrationForm, EntityTaxRegistrationDto>
          isOpen={regModalOpen}
          mode={regModalMode}
          record={regRecord}
          noun="Registration"
          size="lg"
          emptyForm={EMPTY_REG_FORM}
          populateOnOpen={populateRegistration}
          validate={validateRegistration}
          onSave={saveRegistration}
          confirmSaveMessage={(mode, form) => (mode === 'edit'
            ? 'Save the changes to this registration? Documents issued under it from now on use the new details.'
            : `Add this registration${form.isDefault ? ' as the default' : ''}?`)}
          successMessage={(mode) => (mode === 'edit' ? 'Registration updated.' : 'Registration added.')}
          tabId={tabId}
          moduleName={moduleName}
          createInstanceId={String(regCreateCounter.current)}
          recordKey={regRecord?.registrationNumber}
          getStripFormLabel={(f) => f.label || f.registrationNumber || undefined}
          onClose={() => setRegModalOpen(false)}
          onSaved={() => { setRegModalOpen(false); void loadTaxData(); }}
          onRestoreModal={() => setRegModalOpen(true)}
        >
          {({ form, setForm, getFieldError, clearFieldError, isSaving }) => {
            const upd = (patch: Partial<RegistrationForm>) => setForm(prev => ({ ...prev, ...patch }));
            const taxErrors: Record<string, string> = {};
            regFields.forEach(f => { const e = getFieldError(`tax.${f.fieldKey}`); if (e) taxErrors[f.fieldKey] = e; });
            return (
              <>
                <div className="form-grid-2">
                  <div className="form-group">
                    <label className="form-label">Label</label>
                    <input className="form-input" value={form.label} maxLength={100} disabled={isSaving}
                      placeholder={jurisdiction.isStateBased ? 'e.g. Karnataka GSTIN' : 'e.g. Head office VAT'}
                      onChange={e => upd({ label: e.target.value })} />
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>Optional; shown instead of the number in lists.</div>
                  </div>
                  {!schemaHasNumber && (
                    <div className="form-group">
                      <label className="form-label">Registration number <span className="text-danger">*</span></label>
                      <input className={`form-input${getFieldError('registrationNumber') ? ' is-invalid' : ''}`} value={form.registrationNumber} maxLength={50} disabled={isSaving}
                        onChange={e => { clearFieldError('registrationNumber'); upd({ registrationNumber: e.target.value }); }} />
                      {getFieldError('registrationNumber') && <div className="text-danger" style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>{getFieldError('registrationNumber')}</div>}
                    </div>
                  )}
                </div>

                {/* The jurisdiction's REGISTRATION fields: GSTIN + state + type, or a VAT number. */}
                <TaxFieldsPanel context={regContext} values={form.tax} bare disabled={isSaving} errors={taxErrors}
                  onChange={(key, value) => { clearFieldError(`tax.${key}`); upd({ tax: { ...form.tax, [key]: value } }); }} />

                <div className="form-grid-2" style={{ marginTop: '0.75rem' }}>
                  <div className="form-group">
                    <label className="form-label">Effective from</label>
                    <input type="date" className="form-input" value={form.effectiveFrom} disabled={isSaving} onChange={e => upd({ effectiveFrom: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Effective to</label>
                    <input type="date" className={`form-input${getFieldError('effectiveTo') ? ' is-invalid' : ''}`} value={form.effectiveTo} disabled={isSaving}
                      onChange={e => { clearFieldError('effectiveTo'); upd({ effectiveTo: e.target.value }); }} />
                    {getFieldError('effectiveTo') && <div className="text-danger" style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>{getFieldError('effectiveTo')}</div>}
                  </div>
                </div>

                <div className="form-group">
                  <SyutSelect label="Business units issuing under this registration" isMulti isClearable menuPortalTarget={document.body}
                    options={entityBusinessUnits} value={form.businessUnitIds} isDisabled={isSaving}
                    onChange={v => upd({ businessUnitIds: Array.isArray(v) ? v : v ? [v] : [] })}
                    placeholder={form.isDefault ? 'Every unit not linked elsewhere (default)' : 'None linked'} />
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                    A business unit issues under exactly one registration; linking it here removes it from any other.
                  </div>
                </div>

                <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input type="checkbox" id="reg-default" checked={form.isDefault} disabled={isSaving} onChange={e => upd({ isDefault: e.target.checked })} />
                  <label htmlFor="reg-default" style={{ fontSize: '0.8125rem', cursor: 'pointer' }}>Default registration for this entity (used by business units with no link)</label>
                </div>
              </>
            );
          }}
        </EnterpriseFormModal>
      )}

      {jurisdiction && (
        <TaxMasterPicker
          isOpen={pickerOpen}
          title="Choose the default tax master"
          recentlyUsedKey="tax-settings.default-master"
          load={(q) => taxMasterService.list(jurisdiction.jurisdictionCode, q)}
          onSelect={(m) => { setDefaultRateCode(m.rateCode); setDefaultRateName(m.name); setPickerOpen(false); }}
          onClose={() => setPickerOpen(false)}
        />
      )}

      <ConfirmDialog
        open={jurConfirmOpen}
        title="Save tax jurisdiction?"
        message={`Save "${jurisdictions.find(j => j.jurisdictionCode === jurisdictionCode)?.jurisdictionName || jurisdictionCode}" as the tax jurisdiction for this entity? This drives tax fields on masters and the tax calculation.`}
        confirmLabel="Save"
        cancelLabel="Cancel"
        variant="default"
        loading={savingJur}
        loadingMessage="Saving..."
        onConfirm={handleConfirmSaveJurisdiction}
        onCancel={() => setJurConfirmOpen(false)}
      />

      <ConfirmDialog
        open={!!regToDelete}
        title="Delete registration?"
        message={`Delete "${regToDelete?.label || regToDelete?.registrationNumber}"? Business units linked to it fall back to the default registration. Documents already issued keep their snapshots.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={deletingReg}
        loadingMessage="Deleting..."
        onConfirm={handleConfirmDeleteRegistration}
        onCancel={() => setRegToDelete(null)}
      />

      <ConfirmDialog
        open={defaultsConfirmOpen}
        title="Save tax defaults?"
        message={defaultRateCode
          ? `New items under ${jurisdiction?.jurisdictionCode} will be pre-mapped to ${defaultRateName || defaultRateCode}. Existing items are not changed.`
          : 'Clear the default tax master? New items will start unmapped.'}
        confirmLabel="Save"
        cancelLabel="Cancel"
        variant="default"
        loading={savingDefaults}
        loadingMessage="Saving..."
        onConfirm={handleConfirmSaveDefaults}
        onCancel={() => setDefaultsConfirmOpen(false)}
      />

      <ConfirmDialog
        open={applyConfirmOpen}
        title="Apply the default tax master?"
        message={`Pin ${taxConfig?.unmappedItemCount ?? 0} unmapped item(s) to ${taxConfig?.defaultRateName || taxConfig?.defaultRateCode}? Items already mapped are not changed. You can re-map any item under Service Master afterwards.`}
        confirmLabel="Apply"
        cancelLabel="Cancel"
        variant="default"
        loading={applying}
        loadingMessage="Applying..."
        onConfirm={handleConfirmApplyDefault}
        onCancel={() => setApplyConfirmOpen(false)}
      />

      <ConfirmDialog
        open={ledgersConfirmOpen}
        title="Save tax ledgers?"
        message="Save the output and input ledger mappings? The posting engine will use these ledgers for all future tax postings."
        confirmLabel="Save"
        cancelLabel="Cancel"
        variant="default"
        loading={savingLedgers}
        loadingMessage="Saving..."
        onConfirm={handleConfirmSaveLedgers}
        onCancel={() => setLedgersConfirmOpen(false)}
      />
    </EnterpriseScreenShell>
  );
};

export default TaxSettingsScreen;
